import React from "react";
import { clientLog } from "../lib/client-logger";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    clientLog.error('ErrorBoundary', error.message, { stack: error.stack, componentStack: info.componentStack });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            background: "#09090b",
            color: "#fafafa",
            fontFamily: "system-ui, sans-serif",
            padding: 32,
            textAlign: "center",
          }}
        >
          {/* Tia Silverfall avatar — inline styles, no external deps (ErrorBoundary must work when everything is broken) */}
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: '#8B5CF6',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              fontWeight: 600,
              marginBottom: 16,
            }}
            role="img"
            aria-label="Тиа Сильверфолл"
          >
            Т
          </div>
          <h1 style={{ fontSize: 24, marginBottom: 12 }}>Ой, что-то пошло не так...</h1>
          <p style={{ color: "#a1a1aa", marginBottom: 8, maxWidth: 480 }}>
            Не волнуйтесь — я попробую разобраться. А пока можно перезагрузить страницу.
          </p>
          <pre
            style={{
              background: "#18181b",
              padding: 16,
              borderRadius: 8,
              fontSize: 13,
              color: "#ef4444",
              maxWidth: 600,
              overflow: "auto",
              marginBottom: 16,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {this.state.error?.message}
          </pre>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                background: "#27272a",
                color: "#fafafa",
                border: "1px solid #3f3f46",
                cursor: "pointer",
              }}
            >
              Попробовать снова
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                background: "#fafafa",
                color: "#09090b",
                border: "none",
                cursor: "pointer",
              }}
            >
              Перезагрузить
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
